import { useState, useEffect, useRef } from "react";
import { getBrowserIanaTimeZone } from "@/lib/browser-timezone";
import { fetchWorkspaceAnalytics } from "@/services/workspaceService";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
const METRICS_CACHE_TTL_MS = 60_000; // 1 minute

export interface DashboardMetrics {
  projects: number;
  chats: number;
  experiments: number;
  learnings: number;
  insights: number;
  documents: number;
  medianLearningCycleDays: number | null;
  learningCycleSampleSize: number;
  loading: boolean;
  error: string | null;
}
const emptyCounts: Omit<DashboardMetrics, "loading" | "error"> = {
  projects: 0,
  chats: 0,
  experiments: 0,
  learnings: 0,
  insights: 0,
  documents: 0,
  medianLearningCycleDays: null,
  learningCycleSampleSize: 0
};
let metricsCache: {
  workspaceId: string;
  timezone: string;
  data: DashboardMetrics;
  loadedAt: number;
} | null = null;
export function useDashboardMetrics(): DashboardMetrics {
  const currentWorkspaceId = useWorkspaceStore(s => s.currentWorkspaceId);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    ...emptyCounts,
    loading: true,
    error: null
  });
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    if (!currentWorkspaceId) {
      setMetrics({
        ...emptyCounts,
        loading: false,
        error: null
      });
      return () => {
        mountedRef.current = false;
      };
    }
    const workspaceId = currentWorkspaceId;
    const timezone = getBrowserIanaTimeZone();
    const now = Date.now();
    if (metricsCache && metricsCache.workspaceId === workspaceId && metricsCache.timezone === timezone && now - metricsCache.loadedAt < METRICS_CACHE_TTL_MS) {
      setMetrics(metricsCache.data);
      return () => {
        mountedRef.current = false;
      };
    }
    async function loadMetrics() {
      setMetrics(prev => ({
        ...prev,
        loading: true,
        error: null
      }));
      try {
        const data = await fetchWorkspaceAnalytics(workspaceId);
        if (!mountedRef.current) return;
        const next: DashboardMetrics = {
          projects: data.projects,
          chats: data.chats,
          experiments: data.experiments,
          learnings: data.learnings,
          insights: data.insights,
          documents: data.documents,
          medianLearningCycleDays: data.medianLearningCycleDays ?? null,
          learningCycleSampleSize: data.learningCycleSampleSize,
          loading: false,
          error: null
        };
        setMetrics(next);
        metricsCache = {
          workspaceId,
          timezone,
          data: next,
          loadedAt: Date.now()
        };
      } catch (error: unknown) {
        if (!mountedRef.current) return;
        const message = error instanceof Error ? error.message : "Failed to load workspace analytics";
        setMetrics(prev_0 => ({
          ...prev_0,
          loading: false,
          error: message
        }));
      }
    }
    loadMetrics();
    return () => {
      mountedRef.current = false;
    };
  }, [currentWorkspaceId]);
  return metrics;
}